// src/services/liveAttendance.js
// Firestore helpers for live class attendance tracking (auto-completion at ≥75%).
// Collection: liveAttendance
// Doc ID: ${userId}_${classId}      ← classId = source liveClasses doc id
// Schema: {
//   userId,
//   classId,
//   userName,         ← denormalized for webhook context (avoids Clerk lookup)
//   videoTitle,       ← denormalized so the webhook writes the completion title without a follow-up read
//   watchedSeconds,   ← cumulative seconds spent in 'playing' state; last-write-wins via setDoc({merge: true})
//   firstSeenAt,      ← serverTimestamp on first heartbeat (set only via setIfMissing semantics)
//   lastUpdatedAt,    ← serverTimestamp on each heartbeat write
//   processed: false  ← flipped true by mux-webhook after attendance evaluation
// }
//
// LivePlayer accumulates watch time client-side (timeupdate-based) and writes here
// on a 30s heartbeat plus a final flush on cleanup. The mux-webhook reads this
// collection when finalizing a class (live_stream_completed for live, asset.ready
// for pre-recorded) and creates `completions` records for users who watched ≥75%.
//
// See `docs/decisions/adr-auto-completion.md` for the full design.

import { db, firebaseEnabled } from './firebase';
import { doc, serverTimestamp } from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import { setDoc } from './firestoreWrite';

// ── Record (or update) a live attendance heartbeat ──
// Best-effort: errors are swallowed so a transient Firestore failure can't disrupt
// the LivePlayer's user-facing experience. The next heartbeat will retry. The 30s
// heartbeat cadence means at worst we lose 30s of progress on a transient outage.
//
// Uses { merge: true } so subsequent writes update watchedSeconds + lastUpdatedAt
// without clobbering server-owned fields.
//
// Critically, this function does NOT write the `processed` flag — that flag is
// owned by the webhook (api/_attendance-reconcile.js). If a heartbeat lands AFTER
// the webhook flipped processed:true (e.g. a viewer watching the thank-you
// overlay), this write must NOT clobber it. With merge:true and `processed`
// absent from the payload, the prior value is preserved.
//
// `firstSeenAt` is also not written — it's audit-only and not needed by the
// webhook. Adding it would either be a write-every-time clobber (semantically
// wrong) or require a getDoc-before-write pattern (doubles cost). We get
// effectively the same audit signal from `lastUpdatedAt` plus the createdAt-
// equivalent that Firestore implicitly stamps on the doc.
export async function recordLiveAttendance(userId, classId, watchedSeconds, metadata = {}) {
    if (!firebaseEnabled || !db || !userId || !classId) return false;
    if (typeof watchedSeconds !== 'number' || !Number.isFinite(watchedSeconds) || watchedSeconds < 0) return false;
    try {
        const id = `${userId}_${classId}`;
        const ref = doc(db, 'liveAttendance', id);
        const payload = {
            userId,
            classId,
            watchedSeconds: Math.round(watchedSeconds), // integer seconds; saves Firestore bytes + simplifies webhook math
            lastUpdatedAt: serverTimestamp(),
        };
        if (metadata.videoTitle) payload.videoTitle = metadata.videoTitle;
        if (metadata.classScheduledAt) payload.classScheduledAt = metadata.classScheduledAt;
        if (metadata.userName) payload.userName = metadata.userName;
        await setDoc(ref, payload, { merge: true });
        return true;
    } catch (e) {
        // Deliberate console.warn (not error) — best-effort heartbeat shouldn't
        // pollute Sentry with retriable network failures.
        console.warn('liveAttendance write failed (non-fatal):', e?.message || e);
        return false;
    }
}
